import { addMonths, contributionDateForMonth, contributionDates, localDateParts } from './dates';
import { buildForecast } from './forecast';
import {
  assertMoney,
  assertNonNegativeMoney,
  assertPositiveMoney,
  ceilDivideMoney,
  subtractMoney,
} from './money';
import type {
  CashFlowEvent,
  GoalFeasibilityInput,
  GoalFeasibilityResult,
  LocalDate,
} from './types';

export function evaluateGoal(input: GoalFeasibilityInput): GoalFeasibilityResult {
  assertPositiveMoney(input.targetAmount, 'Goal target');
  assertNonNegativeMoney(input.currentAllocated, 'Current goal allocation');
  assertNonNegativeMoney(input.plannedMonthlyContribution, 'Planned contribution');
  // forecastMonthlyFreeCash is income minus planned expenses; a monthly cash-flow
  // deficit is a valid financial state, so only its numeric validity is enforced here.
  assertMoney(input.forecastMonthlyFreeCash, 'Forecast monthly free cash');

  const remaining = Math.max(0, subtractMoney(input.targetAmount, input.currentAllocated));
  const contributionDay = input.contributionDay ?? localDateParts(input.asOfDate).day;
  const dates = contributionDates(input.asOfDate, input.targetDate, contributionDay);
  const periodsRemaining = dates.length;
  const requiredMonthlyContributionAvailable = remaining === 0 || periodsRemaining > 0;
  const requiredMonthlyContribution =
    remaining === 0 ? 0 : periodsRemaining > 0 ? ceilDivideMoney(remaining, periodsRemaining) : 0;
  const contributionGap = requiredMonthlyContributionAvailable
    ? Math.max(0, subtractMoney(requiredMonthlyContribution, input.plannedMonthlyContribution))
    : remaining;

  let forecast: GoalFeasibilityResult['forecast'];
  let cashFlowFeasible = input.plannedMonthlyContribution <= input.forecastMonthlyFreeCash;
  const assumptions = [
    'No investment return or growth is assumed.',
    `Contributions are scheduled on day ${contributionDay}, clamped to month-end.`,
  ];

  if (input.forecastInput && input.fundingAccountId && input.plannedMonthlyContribution > 0) {
    const fundingAccountId = input.fundingAccountId;
    const contributionEvents: CashFlowEvent[] = dates
      .filter((date) => date >= input.forecastInput!.from && date <= input.forecastInput!.to)
      .map((date, index) => ({
        id: `goal-contribution:${date}:${index}`,
        date,
        label: 'Committed goal contribution',
        amount: input.plannedMonthlyContribution,
        sourceAccountId: fundingAccountId,
        kind: 'GOAL_ALLOCATION',
        commitmentLevel: 'COMMITTED',
      }));
    forecast = buildForecast({
      ...input.forecastInput,
      events: [...input.forecastInput.events, ...contributionEvents],
    });
    cashFlowFeasible =
      !forecast.negativeAccountBalanceBreach &&
      !forecast.accountMinimumBreach &&
      !forecast.safetyBufferBreach &&
      !forecast.goalReservedBalanceBreach;
    if (input.targetDate > input.forecastInput.to) {
      assumptions.push(
        `Detailed liquidity is validated through ${input.forecastInput.to}; later contributions require a refreshed forecast.`,
      );
    }
  } else {
    assumptions.push(
      'Cash-flow feasibility uses the supplied monthly free-cash estimate because no funding-account forecast was supplied.',
    );
  }

  const cashFlowFeasibilityReason: GoalFeasibilityResult['cashFlowFeasibilityReason'] =
    cashFlowFeasible
      ? 'FEASIBLE'
      : input.forecastMonthlyFreeCash < 0
        ? 'FREE_CASH_DEFICIT'
        : 'OTHER_CONSTRAINT';

  const feasibleAtPlannedContribution =
    requiredMonthlyContributionAvailable && contributionGap === 0 && cashFlowFeasible;
  const projectedCompletionDate = completionDate(
    input.asOfDate,
    contributionDay,
    remaining,
    input.plannedMonthlyContribution,
  );

  return {
    remaining,
    monthsToTarget: periodsRemaining,
    periodsRemaining,
    requiredMonthlyContribution,
    requiredMonthlyContributionAvailable,
    contributionGap,
    feasibleAtPlannedContribution,
    cashFlowFeasible,
    cashFlowFeasibilityReason,
    forecastMonthlyFreeCash: input.forecastMonthlyFreeCash,
    projectedCompletionDate,
    contributionDates: dates,
    assumptions,
    ...(forecast ? { forecast } : {}),
  };
}

function completionDate(
  asOfDate: LocalDate,
  contributionDay: number,
  remaining: number,
  plannedContribution: number,
): LocalDate | null {
  if (remaining === 0) return asOfDate;
  if (plannedContribution === 0) return null;
  const contributionsNeeded = Math.ceil(remaining / plannedContribution);
  const first = contributionDateForMonth(asOfDate, contributionDay);
  const firstEligible =
    first >= asOfDate ? first : contributionDateForMonth(addMonths(asOfDate, 1), contributionDay);
  return contributionDateForMonth(
    addMonths(firstEligible, contributionsNeeded - 1),
    contributionDay,
  );
}
